import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { HEALTH_COLORS } from "@/lib/constants";
import { Loader2, CheckCircle2, UserCheck, ShieldCheck, CalendarClock } from "lucide-react";

export default function TrueUpDialog({ open, onOpenChange, projectId, onDone }) {
  const [state, setState] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState("");
  const [completing, setCompleting] = useState(false);

  const load = async () => {
    setState(null);
    try {
      const { data } = await api.get(`/projects/${projectId}/trueup`);
      setState(data);
      const d = {};
      (data.pending || []).forEach((t) => {
        d[t.id] = t.proposed_percent != null ? t.proposed_percent : t.percent_complete;
      });
      setDrafts(d);
    } catch (e) {
      toast.error("Could not load true-up");
    }
  };

  useEffect(() => { if (open) load(); }, [open, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const setDraft = (id, v) => setDrafts((d) => ({ ...d, [id]: v }));

  const submitOwner = async (id) => {
    setBusy(`o-${id}`);
    try {
      await api.post(`/tasks/${id}/submit-progress`, { percent: Number(drafts[id] || 0) });
      toast.success("Owner update submitted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const confirmPm = async (id) => {
    setBusy(`c-${id}`);
    try {
      const pct = Number(drafts[id] || 0);
      await api.post(`/tasks/${id}/confirm-progress`, { percent: pct });
      const t = (state?.pending || []).find((x) => x.id === id);
      if (t && (t.status === "done" || (t.percent_complete || 0) >= 100) && pct < 100) {
        toast.info(`"${t.title}" reopened at ${pct}% — it's no longer marked done`);
      } else {
        toast.success("Confirmed by PM");
      }
      setState((s) => s ? { ...s, pending: (s.pending || []).map((t) => (t.id === id ? { ...t, pm_confirmed: true, percent_complete: pct } : t)) } : s);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const completeTrueUp = async () => {
    setCompleting(true);
    try {
      await api.post(`/projects/${projectId}/trueup/complete`);
      toast.success("True-up complete");
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error("Could not complete true-up");
    } finally { setCompleting(false); }
  };

  const pending = state?.pending || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="trueup-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><CalendarClock className="h-5 w-5 text-primary" /> Progress true-up</DialogTitle>
          <DialogDescription>
            Owners submit their % complete, then the PM confirms. Confirmed tasks turn green/blue on the roadmap; unconfirmed tasks stay gray.
          </DialogDescription>
        </DialogHeader>

        {!state ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : pending.length === 0 ? (
          <div className="text-center py-14" data-testid="trueup-empty">
            <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
            <p className="font-semibold">Everything is confirmed and complete.</p>
            <p className="text-sm text-muted-foreground mt-1">No tasks need review right now.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="trueup-list">
            {pending.map((t) => {
              const done = !!t.pm_confirmed;
              return (
              <div key={t.id} className={`border rounded-lg p-3 transition-all ${done ? "border-gray-200 bg-gray-100/70 opacity-75" : "border-border"}`} data-testid={`trueup-row-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className={`text-sm font-semibold truncate ${done ? "text-gray-500" : ""}`}>{t.title}</p>
                    <p className={`text-xs ${done ? "text-gray-400" : "text-muted-foreground"}`}>
                      {t.phase}{(t.responsible || t.assignee) ? ` · ${t.responsible || t.assignee}` : ""}{t.end_date ? ` · due ${t.end_date}` : " · no date"}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {done
                      ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-gray-200 text-gray-600 inline-flex items-center gap-1" data-testid={`trueup-confirmed-badge-${t.id}`}><CheckCircle2 className="h-3 w-3" /> True'd up at {t.percent_complete ?? drafts[t.id] ?? 0}%</span>
                      : t.owner_submitted
                        ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded" style={{ color: HEALTH_COLORS.yellow, background: "#FFFBEB" }}>Awaiting PM</span>
                        : <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-600">Not vetted</span>}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 mt-3">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs w-12 ${done ? "text-gray-400" : "text-muted-foreground"}`}>% done</span>
                    <Input
                      type="number" min="0" max="100"
                      value={drafts[t.id] ?? 0}
                      onChange={(e) => setDraft(t.id, e.target.value)}
                      disabled={done}
                      className="h-8 w-20"
                      data-testid={`trueup-input-${t.id}`}
                    />
                    {t.proposed_percent != null && !done && (
                      <span className="text-[11px] text-muted-foreground">owner proposed {t.proposed_percent}%</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    {!done && (
                      <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => submitOwner(t.id)} disabled={busy === `o-${t.id}`} data-testid={`trueup-submit-${t.id}`}>
                        {busy === `o-${t.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserCheck className="h-3.5 w-3.5" />} Owner submit
                      </Button>
                    )}
                    {done ? (
                      <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-gray-200 text-gray-500 text-xs font-semibold" data-testid={`trueup-done-${t.id}`}>
                        <ShieldCheck className="h-3.5 w-3.5" /> Confirmed
                      </span>
                    ) : (
                      <Button size="sm" className="gap-1.5 h-8" onClick={() => confirmPm(t.id)} disabled={busy === `c-${t.id}`} data-testid={`trueup-confirm-${t.id}`}>
                        {busy === `c-${t.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />} PM confirm
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ); })}
          </div>
        )}

        <DialogFooter className="flex items-center sm:justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            {pending.filter((t) => !t.pm_confirmed).length} pending · cadence: {state?.frequency}{state?.frequency === "custom" ? ` (${state?.custom_days}d)` : ""}
          </p>
          <Button onClick={completeTrueUp} disabled={completing} className="gap-2" data-testid="trueup-complete-button">
            {completing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Mark true-up complete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
